import { codeFromCodeableConcept } from '@seen/core/codeableConcept'
import { isScaleType } from '@seen/core/codings'
import { featureFlags } from '@seen/core/featureFlags'
import {
  BLOOD_PRESSURE_CODING,
  PAIN_SEVERITY_CODING,
} from '@seen/core/models/loinc'
import {
  OBSERVATION_MODE_LATEST,
  recordedObservationAnswers,
} from '@seen/core/observationReferences'
import { observationTemplate } from '@seen/core/terminology/observationTemplates'
import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import { type JSX, useContext } from 'react'
import { match } from 'ts-pattern'
import { useIsNodeConfiguring } from '#src/components/rich-text-editor/conditional-display/useIsNodeConfiguring.ts'
import { hasDerivation } from '#src/components/rich-text-editor/ObservationNode/derived/derivedNode.ts'
import { ObservationNodeDerived } from '#src/components/rich-text-editor/ObservationNode/derived/ObservationNodeDerived.tsx'
import { ObservationDisplayClickable } from '#src/components/rich-text-editor/ObservationNode/ObservationDisplayClickable/ObservationDisplayClickable.tsx'
import { ObservationLatestValue } from '#src/components/rich-text-editor/ObservationNode/ObservationLatestValue.tsx'
import { ObservationNodeBloodPressure } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeBloodPressure.tsx'
import { ObservationNodeChoiceInput } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeChoiceInput.tsx'
import { ObservationNodeComponents } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeComponents.tsx'
import {
  type ConditionDecorationSpec,
  ObservationNodeCondition,
} from '#src/components/rich-text-editor/ObservationNode/observationNodeCondition.ts'
import {
  ObservationNodeConfigure,
  ObservationNodeConfiguring,
} from '#src/components/rich-text-editor/ObservationNode/observationNodeConfig.ts'
import { ObservationNodeDateInput } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeDateInput.tsx'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import { ObservationNodeMultiChoice } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeMultiChoice.tsx'
import { ObservationNodeNumber } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeNumber.tsx'
import { ObservationNodePain } from '#src/components/rich-text-editor/ObservationNode/ObservationNodePain.tsx'
import { ObservationNodeReferenceInput } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeReferenceInput.tsx'
import { ObservationNodeTextInput } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeTextInput.tsx'
import { TemplateAnswerPreview } from '#src/components/rich-text-editor/ObservationNode/TemplateAnswerPreview.tsx'
import type {
  ObservationCaptureViewProps,
  ObservationNodeAttributes,
  ObservationNodeOptions,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import {
  NodeRecordContext,
  useNodeRecord,
  useNodeRecordState,
} from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { useObservationCapture } from '#src/components/rich-text-editor/ObservationNode/useObservationParticipant.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

export function ObservationNodeView(props: NodeViewProps): JSX.Element {
  const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)
  const isReadOnly = useIsReadOnly()
  const extensionOptions = props.extension.options as ObservationNodeOptions
  // Resolved once here for every control the node renders.
  const capture = useObservationCapture(extensionOptions, props.editor)
  const record = useNodeRecordState({
    attributes: props.node.attrs as ObservationNodeAttributes,
    updateAttributes: props.updateAttributes,
    capture,
    onPersisted: extensionOptions.onReferencePersisted,
  })
  // A template places and configures the question. A note answers it, so
  // the configuration sheet and its action are not offered there.
  const configure =
    inlineCapture &&
    extensionOptions.isTemplate &&
    !isReadOnly &&
    props.editor.isEditable
      ? () => {
          const pos = props.getPos()
          if (typeof pos === 'number') {
            props.editor.commands.openNodeConfig(pos)
          }
        }
      : null
  const configuring = useIsNodeConfiguring(props)
  const latest =
    inlineCapture && props.node.attrs.mode === OBSERVATION_MODE_LATEST
  const conditionSpec = props.decorations
    .map((decoration) => decoration.spec as Partial<ConditionDecorationSpec>)
    .find((spec) => typeof spec?.conditionText === 'string')
  const condition = conditionSpec
    ? {
        text: conditionSpec.conditionText ?? '',
        cycle: !!conditionSpec.conditionCycle,
      }
    : null
  const inner = (
    <NodeRecordContext.Provider value={record}>
      <ObservationNodeCondition.Provider value={condition}>
        <ObservationNodeConfigure.Provider value={configure}>
          <ObservationNodeConfiguring.Provider value={configuring}>
            {latest ? (
              <ObservationLatestValue
                attributes={props.node.attrs as ObservationNodeAttributes}
                capture={capture}
              />
            ) : (
              <ObservationNodeViewInner {...props} capture={capture} />
            )}
          </ObservationNodeConfiguring.Provider>
        </ObservationNodeConfigure.Provider>
      </ObservationNodeCondition.Provider>
    </NodeRecordContext.Provider>
  )
  // Free text is the answer itself, so it takes a line of its own (the
  // frame's 'block' layout); the node stays an inline atom in the document.
  if (inlineCapture && !latest && props.node.attrs.scaleType === 'Nar') {
    return (
      <NodeViewWrapper as="div" className="flex w-full text-sm">
        {inner}
      </NodeViewWrapper>
    )
  }
  return (
    <NodeViewWrapper className="inline-flex text-sm">{inner}</NodeViewWrapper>
  )
}

function ObservationNodeViewInner(
  props: ObservationCaptureViewProps
): JSX.Element {
  const { capture } = props
  const attributes = props.node.attrs as ObservationNodeAttributes
  const { scaleType } = attributes
  // Answered: a record the node points at, or a draft it holds.
  const answered = !!attributes.reference || !!attributes.draft
  const extensionOptions = props.extension.options as ObservationNodeOptions
  const onReferencePersisted = extensionOptions.onReferencePersisted
  const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)
  // Read from the code, not stored on the node, so notes written before a
  // template existed pick it up.
  const template = inlineCapture ? observationTemplate(attributes) : undefined

  // A template only places and configures its questions; they are answered
  // in each note made from it.
  if (capture.template && !answered) {
    return <TemplateQuestion {...props} />
  }

  // A code with a calculation computes its value from the note's inputs.
  if (inlineCapture && !template && hasDerivation(attributes)) {
    return <ObservationNodeDerived {...props} />
  }

  if (template) {
    return (
      <ObservationNodeComponents
        template={template}
        attributes={attributes}
        updateAttributes={props.updateAttributes}
        participant={capture.participant}
        noParticipant={capture.noParticipant}
        onReferencePersisted={onReferencePersisted}
        editable={capture.editable}
        recordStatus={capture.recordStatus}
      />
    )
  }

  if (attributes.code === codeFromCodeableConcept(BLOOD_PRESSURE_CODING)) {
    return (
      <ObservationNodeBloodPressure
        attributes={attributes}
        updateAttributes={props.updateAttributes}
        participant={capture.participant}
        noParticipant={capture.noParticipant}
        onReferencePersisted={onReferencePersisted}
        editable={capture.editable}
        recordStatus={capture.recordStatus}
      />
    )
  }

  if (attributes.code === codeFromCodeableConcept(PAIN_SEVERITY_CODING)) {
    return (
      <ObservationNodePain
        attributes={attributes}
        updateAttributes={props.updateAttributes}
        participant={capture.participant}
        noParticipant={capture.noParticipant}
        onReferencePersisted={onReferencePersisted}
        editable={capture.editable}
        recordStatus={capture.recordStatus}
      />
    )
  }

  if (attributes.valueKind) {
    return answered ? (
      <ObservationDisplayClickable {...props} />
    ) : (
      <ObservationNodeDateInput {...props} />
    )
  }

  if (scaleType === 'Qn') {
    return (
      <ObservationNodeNumber
        attributes={attributes}
        updateAttributes={props.updateAttributes}
        participant={capture.participant}
        noParticipant={capture.noParticipant}
        onReferencePersisted={onReferencePersisted}
        editable={capture.editable}
        recordStatus={capture.recordStatus}
      />
    )
  }

  // A repeating choice stays one control and records one Observation per
  // selected answer. Without structured capture an empty one is a single
  // choice; answers already recorded stay visible.
  if (
    attributes.repeats &&
    (attributes.answerOptions?.length ?? 0) > 0 &&
    (inlineCapture ||
      recordedObservationAnswers(attributes.references).length > 0)
  ) {
    return <ObservationNodeMultiChoice {...props} />
  }

  // An answer to a kind of question this version does not know shows as
  // such, not through a display that knows only the known kinds.
  if (answered && scaleType && isScaleType(scaleType)) {
    return <ObservationDisplayClickable {...props} />
  }

  return (
    match(scaleType)
      .with('Ord', 'Nom', () => <ObservationNodeChoiceInput {...props} />)
      .with('Nar', () => <ObservationNodeTextInput {...props} />)
      .with('Ref', () => <ObservationNodeReferenceInput {...props} />)
      // A kind of question this version does not know (a note from a newer
      // one) shows its label and any answer, and is not answered here.
      .otherwise(() => <UnsupportedObservation {...props} />)
  )
}

// A question in a template: what the note will ask, with its actions
// (configure, freeze) on the strip and a click opening its configuration.
function TemplateQuestion(props: ObservationCaptureViewProps): JSX.Element {
  const attributes = props.node.attrs as ObservationNodeAttributes
  const configure = useContext(ObservationNodeConfigure)
  return (
    <ObservationNodeFrame
      attributes={attributes}
      updateAttributes={props.updateAttributes}
      details="button"
      onClick={configure ?? undefined}
    >
      <TemplateAnswerPreview attributes={attributes} />
    </ObservationNodeFrame>
  )
}

function UnsupportedObservation(
  props: ObservationCaptureViewProps
): JSX.Element {
  const attributes = props.node.attrs as ObservationNodeAttributes
  const { observation } = useNodeRecord()
  const answer = observation?.valueDisplay
  return (
    <ObservationNodeFrame attributes={attributes} details="none">
      <span
        title="This question can't be answered in this version of Orbit"
        className="px-1 text-muted-foreground"
      >
        {answer || 'Not available here'}
      </span>
    </ObservationNodeFrame>
  )
}
